import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { eq, not, or } from 'tio-ui/utilities';
import { v4 as uuid } from 'uuid';
import { registerDestructor } from '@ember/destroyable';
import fileQueue from 'ember-file-upload/helpers/file-queue';
import FileDropzone from 'ember-file-upload/components/file-dropzone';
import Text from 'tio-reimagined/components/text';
import type { UploadFile, Queue } from 'ember-file-upload';
import type ObservablesService from 'tio-common/services/observables';
import type { ObservableDocumentAttributes } from 'tio-common/services/observables';
import type CableService from 'tio-common/services/cable';
import type ObservableDocumentModel from 'tio-common/models/observable-document';
import type { IntlService } from 'ember-intl';
import type PersonModel from 'tio-common/models/person';
import type Owner from '@ember/owner';
import {
  PhWarningCircle,
  PhCaretDown,
  PhCaretUp,
  PhNumberCircleOne,
  PhNumberCircleTwo,
  PhNumberCircleThree,
  PhCloudArrowUp,
  PhFilePdf,
  PhXCircle,
  PhCircleNotch,
  PhCheckCircle,
  PhWarning,
} from 'ember-phosphor-icons';

const ACCEPTED_FILE_TYPES = '.pdf';
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const VALID_EXTENSIONS = ['pdf'];
const PROCESSING_TIMEOUT_MS = 60_000;

type UploadState = 'idle' | 'file-selected' | 'uploading' | 'processing' | 'success' | 'error';

type BatchStatusAttributes = Partial<ObservableDocumentAttributes> & {
  extractionState?: ObservableDocumentModel['extractionState'];
  reflectionState?: ObservableDocumentModel['reflectionState'];
};

type BatchStatus = {
  id: string;
  recordAttributes: BatchStatusAttributes;
  transition: { event: string };
};

interface IssueNotificationCardSignature {
  Args: {
    /** Header title for the issue */
    title: string;
    /** Description of the action required */
    description: string;
    /** Person whose documents are being uploaded */
    person: PersonModel;
    /** Whether the card starts expanded */
    isExpanded?: boolean;
  };
  Element: HTMLDivElement;
}

const getFileExtension = (filename: string) => {
  return filename.split('.').pop()?.toLowerCase() ?? '';
};

export default class IssueNotificationCard extends Component<IssueNotificationCardSignature> {
  @service declare observables: ObservablesService;
  @service declare cable: CableService;
  @service declare intl: IntlService;
  @service declare fileQueue: Queue;

  @tracked isExpanded = this.args.isExpanded ?? false;
  @tracked state: UploadState = 'idle';
  @tracked upload?: UploadFile;
  @tracked fileValidationError: string | null = null;
  @tracked uploadError: string | null = null;
  @tracked processingTimedOut = false;

  batchId = uuid();
  private subscriptionParams: object | null = null;
  private processingTimer?: ReturnType<typeof setTimeout>;

  constructor(owner: Owner, args: IssueNotificationCardSignature['Args']) {
    super(owner, args);
    registerDestructor(this, () => {
      this.unsubscribe();
      clearTimeout(this.processingTimer);
    });
  }

  get queue() {
    // @ts-expect-error findOrCreate exists on Queue
    return this.fileQueue.findOrCreate(`issue-notification-upload-${this.batchId}`);
  }

  get queueName() {
    return `issue-notification-upload-${this.batchId}`;
  }

  get hasFile() {
    return !!this.upload;
  }

  toggleExpanded = () => {
    this.isExpanded = !this.isExpanded;
  };

  private unsubscribe = () => {
    if (this.subscriptionParams) {
      this.cable.unsubscribe(this.subscriptionParams);
      this.subscriptionParams = null;
    }
  };

  private subscribeToBatchStatus = () => {
    const channelParams = {
      channel: 'ObservableDocumentBatchStatusChannel',
      batch: this.batchId,
    };

    this.subscriptionParams = channelParams;

    this.cable.subscribe(channelParams, {
      received: (data: unknown) => {
        const msg = data as BatchStatus;
        const { id, recordAttributes, transition } = msg;

        this.observables.updateObservableDocument({ ...recordAttributes, id });

        const extractionFailed = ['ExtractionState.FAILED', 'ExtractionState.INVALID'].includes(
          recordAttributes.extractionState ?? ''
        );

        if (extractionFailed) {
          this.unsubscribe();
          clearTimeout(this.processingTimer);
          const isInvalidDocument = recordAttributes.extractionState === 'ExtractionState.INVALID';
          this.uploadError = isInvalidDocument
            ? this.intl.t('observability.wizard.upload.error_invalid_document')
            : this.intl.t('observability.wizard.upload.error_failed');
          this.state = 'error';
          return;
        }

        const reflectionSuccessful = transition.event === 'content_reflected';
        const reflectionFailed = transition.event === 'reflection_failed';

        if (reflectionSuccessful || reflectionFailed) {
          this.unsubscribe();
          clearTimeout(this.processingTimer);

          if (reflectionSuccessful) {
            this.state = 'success';
          } else {
            this.uploadError = this.intl.t('observability.wizard.upload.error_failed');
            this.state = 'error';
          }
        }
      },
    });

    this.processingTimer = setTimeout(() => {
      if (this.state === 'processing') {
        this.processingTimedOut = true;
      }
    }, PROCESSING_TIMEOUT_MS);
  };

  handleFileAdded = (uploadFile: UploadFile) => {
    this.fileValidationError = null;
    this.uploadError = null;
    const file = uploadFile.file;

    const ext = getFileExtension(file.name);
    if (!VALID_EXTENSIONS.includes(ext)) {
      this.fileValidationError = this.intl.t('observability.wizard.upload.invalid_file_type');
      this.queue.remove(uploadFile);
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      this.fileValidationError = this.intl.t('observability.wizard.upload.file_too_large', {
        fileSize: `${(file.size / (1024 * 1024)).toFixed(1)} MB`,
        maxSize: MAX_FILE_SIZE_MB,
      });
      this.queue.remove(uploadFile);
      return;
    }

    this.upload = uploadFile;
    this.state = 'file-selected';
  };

  removeFile = () => {
    this.queue.remove(this.upload);
    this.upload = undefined;
    this.fileValidationError = null;
    this.state = 'idle';
  };

  handleSubmit = async (e: Event) => {
    e.preventDefault();
    if (!this.upload) return;

    this.unsubscribe();
    clearTimeout(this.processingTimer);
    this.processingTimedOut = false;
    this.state = 'uploading';
    this.subscribeToBatchStatus();

    try {
      await this.observables.createObservableDocument(this.upload, {
        person: this.args.person,
        batch: this.batchId,
      });

      this.state = 'processing';
    } catch {
      this.uploadError = this.intl.t('observability.wizard.upload.error_failed');
      this.state = 'error';
      this.unsubscribe();
      clearTimeout(this.processingTimer);
      this.processingTimedOut = false;
    }
  };

  handleDoneClick = (e: Event) => {
    e.preventDefault();
    if (this.state === 'success') {
      this.isExpanded = false;
      return;
    }
    if (this.upload) {
      void this.handleSubmit(e);
    }
  };

  reset = () => {
    this.unsubscribe();
    clearTimeout(this.processingTimer);
    this.queue.remove(this.upload);
    this.upload = undefined;
    this.fileValidationError = null;
    this.uploadError = null;
    this.processingTimedOut = false;
    this.state = 'idle';
    this.batchId = uuid();
  };

  <template>
    {{! Figma: Notification/No Longer Eligible — 4 states:
        collapsed, expanded (no file), expanded (file uploaded), success/processing }}
    <div
      class="bg-white border border-gray-200 flex flex-col gap-6 items-start px-4 py-6 rounded-2xl"
      data-test-issue-notification-card
      ...attributes
    >
      {{! ── Top Row: Icon + Title/Description + Caret Toggle ── }}
      <button
        type="button"
        class="flex items-center justify-between w-full cursor-pointer"
        data-test-issue-notification-toggle
        aria-expanded="{{this.isExpanded}}"
        aria-label={{if
          this.isExpanded
          (t "slr.reimagined.collapse_action_item")
          (t "slr.reimagined.expand_action_item")
        }}
        {{on "click" this.toggleExpanded}}
      >
        <div class="flex gap-3 items-center">
          <div class="flex items-center justify-center rounded-lg shrink-0 size-12 bg-red-100">
            <PhWarningCircle @size={{32}} class="text-red-600" />
          </div>
          <div class="flex flex-col gap-1 items-start text-left">
            <Text
              class="font-semibold text-base text-gray-700 capitalize tracking-tight leading-snug"
            >
              {{@title}}
            </Text>
            <Text class="font-medium text-base text-gray-500 leading-snug">
              {{@description}}
            </Text>
          </div>
        </div>
        <div class="flex items-center justify-center shrink-0 ml-4">
          {{#if this.isExpanded}}
            <PhCaretUp @size={{24}} class="text-gray-700" />
          {{else}}
            <PhCaretDown @size={{24}} class="text-gray-700" />
          {{/if}}
        </div>
      </button>

      {{! ── Expanded Content Area ── }}
      {{#if this.isExpanded}}
        <div
          class="bg-sky-50 flex flex-col items-start p-4 rounded-2xl w-full"
          data-test-issue-notification-expanded
        >
          {{#if (eq this.state "success")}}
            {{! ── Success State ── }}
            <div
              class="flex flex-col items-center justify-center w-full py-8 gap-3"
              data-test-issue-notification-success
            >
              <div class="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100">
                <PhCheckCircle @size={{24}} class="text-emerald-600" />
              </div>
              <Text class="text-lg font-semibold text-emerald-900">
                {{t "observability.wizard.complete.heading"}}
              </Text>
              <Text class="text-sm text-emerald-700">
                {{t
                  "observability.wizard.complete.description"
                  docType=(t "observability.wizard.doc_type.statement")
                }}
              </Text>
              <button
                type="button"
                class="text-sm font-medium text-emerald-700 underline hover:text-emerald-800 cursor-pointer"
                {{on "click" this.reset}}
              >
                {{t "observability.wizard.complete.upload_another"}}
              </button>
            </div>
          {{else if (eq this.state "processing")}}
            {{! ── Processing State ── }}
            <div
              class="flex flex-col items-center justify-center w-full py-8 gap-3"
              data-test-issue-notification-processing
            >
              <div class="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100">
                <PhCircleNotch @size={{24}} class="text-indigo-600 animate-spin" />
              </div>
              <Text class="text-lg font-semibold text-indigo-900">
                {{t "observability.wizard.upload.processing_title"}}
              </Text>
              <Text class="text-sm text-indigo-700">
                {{t "observability.wizard.upload.processing_description"}}
              </Text>
              {{#if this.processingTimedOut}}
                <div
                  class="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-1 w-full max-w-md"
                >
                  <div class="flex items-start gap-2">
                    <PhWarning @size={{16}} class="text-amber-500 shrink-0 mt-0.5" />
                    <Text class="text-xs text-amber-800">
                      {{t "observability.wizard.upload.processing_slow"}}
                    </Text>
                  </div>
                </div>
              {{/if}}
            </div>
          {{else}}
            {{! ── Default: Instructions + Upload ── }}
            <div class="flex flex-col lg:flex-row items-start w-full gap-4">
              {{! Left Column: Instructions }}
              <div
                class="flex flex-col gap-8 items-start flex-1 min-w-0 px-2 sm:px-4 lg:pl-11 lg:pr-6 py-4"
              >
                {{! Intro text }}
                <Text class="font-medium text-base text-gray-700 leading-relaxed">
                  {{t "slr.reimagined.issue_notification.intro"}}
                </Text>

                {{! Steps section }}
                <div class="flex flex-col gap-4 items-start w-full">
                  <Text class="font-semibold text-base text-gray-800 leading-none">
                    {{t "slr.reimagined.issue_notification.steps_heading"}}
                  </Text>

                  <div class="flex flex-col gap-4 items-start pl-2 w-full">
                    {{! Step 1 }}
                    <div class="flex gap-1.5 items-start w-full">
                      <PhNumberCircleOne
                        @size={{26}}
                        class="text-red-600 shrink-0"
                        @weight="fill"
                      />
                      <Text class="font-medium text-base text-gray-700 leading-relaxed flex-1">
                        <span class="font-semibold text-gray-800">{{t
                            "slr.reimagined.issue_notification.step1_label"
                          }}</span>
                        {{t "slr.reimagined.issue_notification.step1_text"}}
                      </Text>
                    </div>

                    {{! Step 2 }}
                    <div class="flex gap-1.5 items-start w-full">
                      <PhNumberCircleTwo
                        @size={{26}}
                        class="text-red-600 shrink-0"
                        @weight="fill"
                      />
                      <Text class="font-medium text-base text-gray-700 leading-relaxed flex-1">
                        <span class="font-semibold text-gray-800">{{t
                            "slr.reimagined.issue_notification.step2_label"
                          }}</span>
                        {{t "slr.reimagined.issue_notification.step2_text"}}
                      </Text>
                    </div>

                    {{! Step 3 }}
                    <div class="flex gap-1.5 items-start w-full">
                      <PhNumberCircleThree
                        @size={{26}}
                        class="text-red-600 shrink-0"
                        @weight="fill"
                      />
                      <Text class="font-medium text-base text-gray-700 leading-relaxed flex-1">
                        <span class="font-semibold text-gray-800">{{t
                            "slr.reimagined.issue_notification.step3_label"
                          }}</span>
                        {{t "slr.reimagined.issue_notification.step3_text"}}
                      </Text>
                    </div>
                  </div>
                </div>

                {{! Can't find it? }}
                <Text class="font-medium text-base text-gray-700 leading-relaxed">
                  <span class="font-semibold text-gray-800">{{t
                      "slr.reimagined.issue_notification.cant_find_label"
                    }}</span>
                  {{t "slr.reimagined.issue_notification.cant_find_text"}}
                </Text>
              </div>

              {{! Right Column: Upload + Done Button }}
              <div
                class="flex flex-col justify-between px-2 sm:px-4 lg:pl-6 lg:pr-11 py-4 w-full lg:w-[45%] self-stretch"
              >
                <div class="flex flex-col flex-1 gap-3 items-start w-full">
                  {{! Upload Dropzone — custom styled to match Figma }}
                  {{#unless this.upload}}
                    {{#let
                      (fileQueue name=this.queueName onFileAdded=this.handleFileAdded)
                      as |queue|
                    }}
                      <FileDropzone @queue={{queue}} as |dropzone|>
                        <label
                          data-test-issue-notification-dropzone
                          class="flex flex-col gap-4 items-center justify-center w-full min-h-[200px] lg:min-h-[250px] bg-gray-200 border border-dashed border-gray-400 rounded-xl cursor-pointer p-4"
                        >
                          {{#if dropzone.active}}
                            <Text class="text-sm text-gray-700">
                              {{t "slr.reimagined.drop_to_upload"}}
                            </Text>
                          {{else}}
                            <div class="flex flex-col gap-2 items-center">
                              <PhCloudArrowUp @size={{40}} class="text-gray-500" />
                              <Text class="font-medium text-base text-center">
                                <span class="font-semibold text-gray-900 underline">{{t
                                    "slr.reimagined.select_a_file"
                                  }}</span>
                                <span class="text-gray-800">
                                  {{t "slr.reimagined.drop_to_upload"}}
                                </span>
                              </Text>
                            </div>
                            <Text class="font-medium text-xs text-gray-700 text-center">
                              {{t "slr.reimagined.issue_notification.supported_format"}}
                            </Text>
                          {{/if}}
                          <input
                            {{queue.selectFile}}
                            type="file"
                            class="sr-only"
                            accept={{ACCEPTED_FILE_TYPES}}
                          />
                        </label>
                      </FileDropzone>
                    {{/let}}
                  {{/unless}}

                  {{! File Tag (when file selected) }}
                  {{#if this.upload}}
                    <div class="flex items-start" data-test-issue-notification-file-tag>
                      <div class="bg-gray-200 flex gap-2 items-center px-1.5 py-0.5 rounded-lg">
                        <div class="flex items-center gap-0.5">
                          <PhFilePdf @size={{14}} class="text-gray-800 shrink-0" />
                          <Text
                            class="font-medium text-xs text-gray-800 leading-4 truncate max-w-[200px]"
                          >
                            {{this.upload.file.name}}
                          </Text>
                        </div>
                        <button
                          type="button"
                          class="cursor-pointer shrink-0"
                          data-test-issue-notification-remove-file
                          aria-label={{t "slr.reimagined.issue_notification.remove_file"}}
                          {{on "click" this.removeFile}}
                        >
                          <PhXCircle @size={{14}} class="text-gray-500" />
                        </button>
                      </div>
                    </div>
                  {{/if}}

                  {{! Validation/upload errors }}
                  {{#if this.fileValidationError}}
                    <div
                      class="rounded-xl border border-red-200 bg-red-50 p-3 flex items-start gap-2 w-full"
                      data-test-issue-notification-validation-error
                    >
                      <PhWarning @size={{16}} class="text-red-500 shrink-0 mt-0.5" />
                      <Text class="text-xs text-red-800">{{this.fileValidationError}}</Text>
                    </div>
                  {{/if}}

                  {{#if (eq this.state "error")}}
                    <div
                      class="rounded-xl border border-red-200 bg-red-50 p-3 flex items-start gap-2 w-full"
                      data-test-issue-notification-upload-error
                    >
                      <PhWarning @size={{16}} class="text-red-500 shrink-0 mt-0.5" />
                      <div>
                        <Text class="text-xs font-medium text-red-800">{{this.uploadError}}</Text>
                        <button
                          type="button"
                          class="text-xs text-red-700 underline mt-1 cursor-pointer"
                          {{on "click" this.reset}}
                        >
                          {{t "observability.wizard.upload.try_again"}}
                        </button>
                      </div>
                    </div>
                  {{/if}}
                </div>

                {{! Done Button }}
                <button
                  type="button"
                  data-test-issue-notification-done
                  class="flex items-center justify-center w-full min-w-[168px] px-3 py-2 rounded-md font-semibold text-sm text-white mt-4 leading-none
                    {{if
                      (or (eq this.state 'uploading') (eq this.state 'processing'))
                      'bg-teal-600 opacity-70 cursor-not-allowed'
                      (if
                        this.hasFile
                        'bg-teal-600 cursor-pointer hover:bg-teal-700'
                        'bg-gray-400 opacity-40 cursor-not-allowed'
                      )
                    }}"
                  disabled={{if
                    (or (eq this.state "uploading") (eq this.state "processing"))
                    true
                    (not this.hasFile)
                  }}
                  {{on "click" this.handleDoneClick}}
                >
                  {{#if (eq this.state "uploading")}}
                    <PhCircleNotch @size={{16}} class="animate-spin mr-2" />
                    {{t "observability.wizard.upload.uploading"}}
                  {{else}}
                    {{t "slr.reimagined.done"}}
                  {{/if}}
                </button>
              </div>
            </div>
          {{/if}}
        </div>
      {{/if}}
    </div>
  </template>
}
